import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useLanguage, t } from './LanguagePicker';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HISTORY_KEY = 'pakdeheru_browse_history';
const MAX_HISTORY = 20;

// --- Browsing history helpers ---
const getHistory = () => {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); }
  catch { return []; }
};

const saveHistory = (h) => {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(h.slice(-MAX_HISTORY)));
};

export const trackArticleView = (article) => {
  if (!article?.title) return;
  const h = getHistory();
  // Avoid duplicate consecutive entries
  if (h.length && h[h.length - 1].title === article.title) return;
  h.push({
    type: 'article',
    title: article.title,
    category: article.category || '',
    tags: (article.tags || []).slice(0, 4),
    ts: Date.now(),
  });
  saveHistory(h);
};

export const trackTagView = (tag) => {
  if (!tag) return;
  const h = getHistory();
  h.push({ type: 'tag', tag, ts: Date.now() });
  saveHistory(h);
};

export const trackPageView = (page) => {
  const h = getHistory();
  if (h.length && h[h.length - 1].page === page) return;
  h.push({ type: 'page', page, ts: Date.now() });
  saveHistory(h);
};

// --- Build personalized greeting ---
const buildGreeting = () => {
  const h = getHistory();
  if (!h.length) return t('chatbot_greeting_default');

  const articles = h.filter(e => e.type === 'article');
  const tags = h.filter(e => e.type === 'tag');

  if (articles.length > 0) {
    const last = articles[articles.length - 1];

    // Count category visits
    const catCounts = {};
    articles.forEach(a => { if (a.category) catCounts[a.category] = (catCounts[a.category] || 0) + 1; });
    const topCat = Object.entries(catCounts).sort((a, b) => b[1] - a[1])[0];

    if (articles.length >= 3 && topCat && topCat[1] >= 2) {
      return t('chatbot_greeting_frequent').replace('{topic}', topCat[0]).replace('{count}', articles.length);
    }

    if (articles.length >= 2) {
      return t('chatbot_greeting_returning').replace('{count}', articles.length).replace('{title}', last.title);
    }

    return t('chatbot_greeting_article').replace('{title}', last.title);
  }

  if (tags.length > 0) {
    const lastTag = tags[tags.length - 1].tag;
    return t('chatbot_greeting_tag').replace('{tag}', lastTag);
  }

  return t('chatbot_greeting_welcome_back');
};

// --- Build browse context for AI ---
const buildBrowseContext = () => {
  const h = getHistory();
  if (!h.length) return '';
  const articles = h.filter(e => e.type === 'article');
  if (!articles.length) return '';
  const titles = [...new Set(articles.map(a => a.title))];
  const cats = [...new Set(articles.map(a => a.category).filter(Boolean))];
  return `[Konteks browsing user: Sudah membaca ${titles.length} artikel. Artikel terakhir: "${titles[titles.length - 1]}". Kategori yang diminati: ${cats.join(', ') || 'belum terdeteksi'}.]`;
};

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => 'chat-' + Math.random().toString(36).slice(2, 10));
  const [greeted, setGreeted] = useState(false);
  const messagesEnd = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const lang = useLanguage();
  const [, forceRender] = useState(0);

  // Track page navigation
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Generate greeting when opening
  const handleOpen = useCallback(() => {
    setOpen(true);
    if (!greeted) {
      const greeting = buildGreeting();
      setMessages([{ role: 'bot', html: greeting }]);
      setGreeted(true);
    }
  }, [greeted]);

  // Re-render when translations arrive
  useEffect(() => {
    const handler = () => forceRender(n => n + 1);
    window.addEventListener('ui-translations-loaded', handler);
    return () => window.removeEventListener('ui-translations-loaded', handler);
  }, []);

  // Re-generate greeting when language changes or translations arrive
  useEffect(() => {
    const regenerate = () => {
      setMessages(prev => {
        if (prev.length > 0 && prev[0].role === 'bot') {
          const greeting = buildGreeting();
          return [{ role: 'bot', html: greeting }, ...prev.slice(1)];
        }
        return prev;
      });
    };
    if (greeted) {
      regenerate();
      window.addEventListener('ui-translations-loaded', regenerate);
      return () => window.removeEventListener('ui-translations-loaded', regenerate);
    }
  }, [lang, greeted]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const sendMessage = async (e) => {
    e.preventDefault();
    const msg = input.trim();
    if (!msg || loading) return;

    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setInput('');
    setLoading(true);

    try {
      const browseCtx = buildBrowseContext();
      const res = await axios.post(`${BACKEND_URL}/api/chat`, {
        message: browseCtx ? `${browseCtx}\n\n${msg}` : msg,
        session_id: sessionId,
        language: lang,
      });
      setMessages(prev => [...prev, { role: 'bot', html: res.data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'bot', html: t('chatbot_error') }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button className="chatbot-fab" onClick={handleOpen} data-testid="chatbot-fab" aria-label="Open chat">
          <MessageCircle size={24} />
        </button>
      )}

      {open && (
        <div className="chatbot-window" data-testid="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <Bot size={20} />
              <div>
                <span className="chatbot-header-title">{t('chatbot_title')}</span>
                <span className="chatbot-header-sub">{t('chatbot_subtitle')}</span>
              </div>
            </div>
            <button className="chatbot-close" onClick={() => setOpen(false)} data-testid="chatbot-close">
              <X size={18} />
            </button>
          </div>

          <div className="chatbot-messages" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`chatbot-msg chatbot-msg-${m.role}`}>
                <div className="chatbot-msg-avatar">
                  {m.role === 'bot' ? <Bot size={14} /> : <User size={14} />}
                </div>
                {m.html ? (
                  <div className="chatbot-msg-bubble" dangerouslySetInnerHTML={{ __html: m.html }} />
                ) : (
                  <div className="chatbot-msg-bubble">{m.text}</div>
                )}
              </div>
            ))}
            {loading && (
              <div className="chatbot-msg chatbot-msg-bot">
                <div className="chatbot-msg-avatar"><Bot size={14} /></div>
                <div className="chatbot-msg-bubble chatbot-typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={messagesEnd} />
          </div>

          <form className="chatbot-input-form" onSubmit={sendMessage} data-testid="chatbot-form">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={t('chatbot_placeholder')}
              disabled={loading}
              data-testid="chatbot-input"
            />
            <button type="submit" disabled={loading || !input.trim()} data-testid="chatbot-send">
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default Chatbot;
